<!-- src/views/admin/Devices.vue -->
<template>
  <admin-layout :title="pageTitle">
    <div class="mb-6 flex justify-between items-center">
      <div class="flex space-x-2">
        <router-link
          to="/admin/devices"
          class="px-3 py-1.5 rounded text-sm font-medium"
          :class="filter !== 'offline' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'">
          Tous ({{ totalCount }})
        </router-link>
        <router-link
          to="/admin/devices?filter=offline"
          class="px-3 py-1.5 rounded text-sm font-medium"
          :class="filter === 'offline' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'">
          Hors ligne
        </router-link>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
    </div>

    <div v-else-if="devices.length === 0" class="bg-white rounded-lg shadow p-6 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">
        {{ filter === 'offline' ? 'Aucun dispositif hors ligne' : 'Aucun dispositif' }}
      </h3>
    </div>

    <div v-else class="bg-white shadow overflow-hidden sm:rounded-md">
      <ul class="divide-y divide-gray-200">
        <li v-for="device in devices" :key="device.id">
          <router-link
            :to="`/admin/devices/${device.id}`"
            class="block hover:bg-gray-50">
            <div class="px-4 py-4 sm:px-6">
              <div class="flex items-center justify-between">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center">
                    <p class="font-medium text-primary-600 truncate">
                      {{ device.name || 'Sans nom' }}
                    </p>
                    <span v-if="!device.active" class="ml-2 text-xs text-red-500">
                      (Désactivé)
                    </span>
                    <span
                      class="ml-3 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                      :class="isOffline(device) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'">
                      {{ isOffline(device) ? 'Hors ligne' : 'En ligne' }}
                    </span>
                  </div>
                  <div class="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500">
                    <span>Client : <strong class="text-gray-700">{{ device.client_name }}</strong></span>
                    <span v-if="device.ident">IMEI : {{ device.ident }}</span>
                    <span>Dernière connexion : {{ formatDate(device.last_seen) }}</span>
                    <span>
                      Firmware :
                      <strong v-if="device.fw_ver != null" class="text-gray-700">{{ fwLabel(device.fw_ver) }}</strong>
                      <span v-else class="text-gray-400">Pré-versionnement</span>
                    </span>
                  </div>
                </div>
                <svg class="ml-4 h-5 w-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </router-link>
        </li>
      </ul>
    </div>
  </admin-layout>
</template>

<script>
import { FUSEAU } from '@/i18n/dates';
import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import AdminLayout from '@/components/layout/AdminLayout.vue';
import apiService from '@/services/api.service';
import { formatVersion } from '@/utils/fwVersion';

const OFFLINE_MS = 90 * 60 * 1000;

export default defineComponent({
  name: 'AdminDevices',

  components: { AdminLayout },

  setup() {
    const route = useRoute();
    const devices = ref([]);
    const totalCount = ref(0);
    const loading = ref(true);

    const filter = computed(() => route.query.filter || null);

    const pageTitle = computed(() =>
      filter.value === 'offline' ? 'Dispositifs hors ligne' : 'Dispositifs'
    );

    const fetchDevices = async () => {
      loading.value = true;
      try {
        const status = filter.value === 'offline' ? 'offline' : null;
        const { data } = await apiService.getAllDevices(status);
        devices.value = data;

        if (status === null) {
          totalCount.value = data.length;
        } else {
          // récupère le total séparément pour l'afficher dans l'onglet "Tous"
          const all = await apiService.getAllDevices();
          totalCount.value = all.data.length;
        }
      } catch (err) {
        console.error('Erreur chargement devices:', err);
        devices.value = [];
      } finally {
        loading.value = false;
      }
    };

    // Règle du SERVEUR (`presence`, cadence propre à chaque carte, 05/10/2026). Le repli sur
    // 90 min ne sert qu'à une réponse antérieure à ce champ.
    const isOffline = (device) => {
      if (device.presence) return device.presence.hors_ligne;
      if (!device.last_seen) return true;
      return (Date.now() - new Date(device.last_seen).getTime()) > OFFLINE_MS;
    };

    const formatDate = (iso) => {
      if (!iso) return 'Jamais';
      const d = new Date(iso);
      return d.toLocaleString('fr-FR', { timeZone: FUSEAU,
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    };

    const fwLabel = (v) => formatVersion(v);

    onMounted(fetchDevices);
    watch(filter, fetchDevices);

    return { devices, totalCount, loading, filter, pageTitle, isOffline, formatDate, fwLabel };
  }
});
</script>
